import React, { useState, useEffect } from 'react';
import axios from '../utils/axiosConfig';
import { toast } from 'react-toastify';

interface Kasa {
    kasaID: number;
    kasaAdi: string;
    kasaTuru: string; // 'Nakit' veya 'Banka'
    paraBirimiID: number;
    paraBirimi: string;
    kullaniciID: number;
    sorumlu: string;
    bakiye: number;
}

interface KasaForm {
    kasaAdi: string;
    kasaTuru: number; // Form'da number (0/1), backend enum'a çevirir
    paraBirimiID: number;
    kullaniciID: number;
}

interface ParaBirimi {
    paraBirimiID: number;
    birim: string;
    carpan: number;
}

interface Kullanici {
    kullaniciID: number;
    adSoyad: string;
}

const Kasalar: React.FC = () => {
    const [kasalar, setKasalar] = useState<Kasa[]>([]);
    const [paraBirimleri, setParaBirimleri] = useState<ParaBirimi[]>([]);
    const [kullanicilar, setKullanicilar] = useState<Kullanici[]>([]);
    const [selectedKasa, setSelectedKasa] = useState<Kasa | null>(null);
    const [showModal, setShowModal] = useState(false);
    const [formData, setFormData] = useState<KasaForm>({
        kasaAdi: '',
        kasaTuru: 0,
        paraBirimiID: 0,
        kullaniciID: 0
    });

    const fetchKasalar = async () => {
        try {
            const response = await axios.get('/api/Kasalar');
            setKasalar(response.data);
        } catch (error) {
            toast.error('Kasalar yüklenirken bir hata oluştu');
        }
    };

    const fetchParaBirimleri = async () => {
        try {
            const response = await axios.get('/api/ParaBirimleri');
            setParaBirimleri(response.data);
        } catch (error) {
            toast.error('Para birimleri yüklenirken bir hata oluştu');
        }
    };

    const fetchKullanicilar = async () => {
        try {
            const response = await axios.get('/api/Kullanicilar/aktif-liste');
            setKullanicilar(response.data);
        } catch (error) {
            toast.error('Kullanıcılar yüklenirken bir hata oluştu');
        }
    };

    useEffect(() => {
        fetchKasalar();
        fetchParaBirimleri();
        fetchKullanicilar();
    }, []);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            if (selectedKasa) {
                // Güncelleme
                await axios.put(`/api/Kasalar/${selectedKasa.kasaID}`, formData);
                toast.success('Kasa güncellendi');
            } else {
                // Yeni ekleme
                await axios.post('/api/Kasalar', formData);
                toast.success('Kasa eklendi');
            }
            fetchKasalar();
            handleCloseModal();
        } catch (error) {
            toast.error('İşlem sırasında bir hata oluştu');
        }
    };

    const handleEdit = (kasa: Kasa) => {
        setSelectedKasa(kasa);
        setFormData({
            kasaAdi: kasa.kasaAdi,
            kasaTuru: kasa.kasaTuru === 'Banka' ? 1 : 0,
            paraBirimiID: kasa.paraBirimiID,
            kullaniciID: kasa.kullaniciID
        });
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedKasa(null);
        setFormData({
            kasaAdi: '',
            kasaTuru: 0,
            paraBirimiID: 0,
            kullaniciID: 0
        });
    };

    const handleKasaSil = async (kasa: Kasa) => {
        if (!window.confirm(`"${kasa.kasaAdi}" kasasını silmek istediğinizden emin misiniz?\nBu işlem geri alınamaz.`)) return;
        try {
            await axios.delete(`/api/Kasalar/${kasa.kasaID}`);
            toast.success('Kasa silindi');
            fetchKasalar();
        } catch (error: any) {
            const msg = error.response?.data?.message || 'Kasa silinirken bir hata oluştu';
            toast.error(msg);
        }
    };

    const handleShowModal = () => { setShowModal(true); };

    // ── Derived stats ──
    const tryKasalar = kasalar.filter(k => k.paraBirimi === 'TRY' || k.paraBirimi === 'TL' || !k.paraBirimi);
    const bankKasalar = kasalar.filter(k => {
        const n = k.kasaAdi.toLowerCase();
        return n.includes('banka') || n.includes('bank') || n.includes('ziraat') || n.includes('yapı') || n.includes('garanti') || n.includes('akbank') || n.includes('halk') || n.includes('vakıf') || n.includes('bnk');
    });
    const dovizKasalar = kasalar.filter(k => k.paraBirimi && k.paraBirimi !== 'TRY' && k.paraBirimi !== 'TL');

    const toplamNakit = tryKasalar.filter(k => !bankKasalar.includes(k)).reduce((s, k) => s + k.bakiye, 0);
    const bankaBakiye = bankKasalar.reduce((s, k) => s + k.bakiye, 0);
    const dovizToplam = dovizKasalar.reduce((s, k) => s + k.bakiye, 0);

    const fmtTL  = (v: number) => v.toLocaleString('tr-TR', { minimumFractionDigits: 2 }) + ' ₺';
    const fmtNum = (v: number) => v.toLocaleString('tr-TR', { minimumFractionDigits: 2 });

    const isBank = (k: Kasa) => {
        const n = k.kasaAdi.toLowerCase();
        return n.includes('banka') || n.includes('bank') || n.includes('ziraat') || n.includes('yapı') || n.includes('garanti') || n.includes('akbank') || n.includes('halk') || n.includes('vakıf') || n.includes('bnk');
    };

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

                .ka-page {
                    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
                    padding: 28px 32px;
                    background: #f4f6fb;
                    min-height: 100vh;
                }

                /* ── Header ── */
                .ka-header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    margin-bottom: 24px;
                }
                .ka-header-left { display: flex; flex-direction: column; gap: 3px; }
                .ka-title {
                    font-size: 22px;
                    font-weight: 700;
                    color: #0f172a;
                    letter-spacing: -0.4px;
                    margin: 0;
                }
                .ka-subtitle { font-size: 13px; color: #94a3b8; font-weight: 400; }
                .ka-add-btn {
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    padding: 10px 20px;
                    background: #cd6906;
                    color: #fff;
                    border: none;
                    border-radius: 10px;
                    font-size: 14px;
                    font-weight: 600;
                    font-family: inherit;
                    cursor: pointer;
                    box-shadow: 0 4px 14px rgba(205,105,6,0.35);
                    transition: background 0.18s, box-shadow 0.18s, transform 0.12s;
                }
                .ka-add-btn:hover {
                    background: #b85d05;
                    box-shadow: 0 6px 20px rgba(205,105,6,0.45);
                    transform: translateY(-1px);
                }
                .ka-add-btn svg { width: 16px; height: 16px; stroke: #fff; stroke-width: 2.5; fill: none; }

                /* ── Stat Cards ── */
                .ka-stats {
                    display: grid;
                    grid-template-columns: repeat(3, 1fr);
                    gap: 16px;
                    margin-bottom: 24px;
                }
                .ka-stat-card {
                    background: #fff;
                    border-radius: 12px;
                    padding: 14px 16px;
                    box-shadow: 0 1px 4px rgba(15,23,42,0.07), 0 2px 12px rgba(15,23,42,0.04);
                    display: flex;
                    align-items: center;
                    gap: 14px;
                }
                .ka-stat-icon {
                    width: 38px;
                    height: 38px;
                    border-radius: 9px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                }
                .ka-stat-icon svg { width: 20px; height: 20px; fill: none; stroke-width: 1.8; stroke-linecap: round; stroke-linejoin: round; }
                .ka-stat-icon-green  { background: #dcfce7; }
                .ka-stat-icon-green  svg { stroke: #16a34a; }
                .ka-stat-icon-blue   { background: #dbeafe; }
                .ka-stat-icon-blue   svg { stroke: #2563eb; }
                .ka-stat-icon-amber  { background: #fef3c7; }
                .ka-stat-icon-amber  svg { stroke: #d97706; }
                .ka-stat-body { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
                .ka-stat-label { font-size: 11px; color: #94a3b8; font-weight: 400; white-space: nowrap; }
                .ka-stat-value { font-size: 15px; font-weight: 600; color: #0f172a; letter-spacing: -0.2px; }

                /* ── Card ── */
                .ka-card {
                    background: #fff;
                    border-radius: 14px;
                    box-shadow: 0 1px 4px rgba(15,23,42,0.07), 0 4px 24px rgba(15,23,42,0.05);
                    overflow: hidden;
                }

                /* ── Table ── */
                .ka-table { width: 100%; border-collapse: collapse; }
                .ka-table thead tr { border-bottom: 1.5px solid #f1f5f9; }
                .ka-table thead th {
                    padding: 10px 16px;
                    font-size: 11px;
                    font-weight: 500;
                    letter-spacing: 0.4px;
                    color: #94a3b8;
                    text-align: left;
                    white-space: nowrap;
                }
                .ka-table thead th.ka-th-r { text-align: right; }
                .ka-table thead th.ka-th-actions { text-align: right; }
                .ka-table tbody tr {
                    border-bottom: 1px solid #f8fafc;
                    transition: background 0.13s;
                }
                .ka-table tbody tr:last-child { border-bottom: none; }
                .ka-table tbody tr.ka-row-hovered { background: #f0f5ff; }
                .ka-table tbody td {
                    padding: 12px 16px;
                    font-size: 13px;
                    color: #1e293b;
                    vertical-align: middle;
                }

                /* Cell variants */
                .ka-cell-id { font-size: 12px; color: #94a3b8; font-weight: 500; width: 54px; }
                .ka-cell-name {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                }
                .ka-name-icon {
                    width: 34px;
                    height: 34px;
                    border-radius: 9px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                }
                .ka-name-icon svg { width: 16px; height: 16px; fill: none; stroke-width: 1.8; stroke-linecap: round; stroke-linejoin: round; }
                .ka-icon-bank { background: #dbeafe; }
                .ka-icon-bank svg { stroke: #2563eb; }
                .ka-icon-cash { background: #dcfce7; }
                .ka-icon-cash svg { stroke: #16a34a; }
                .ka-name-text { font-weight: 500; color: #0f172a; font-size: 13px; }
                .ka-cell-para { font-size: 11px; color: #64748b; font-weight: 500; }
                .ka-cell-sorumlu { font-size: 12px; color: #475569; }
                .ka-cell-bakiye { text-align: right; }
                .ka-bakiye-pos { font-weight: 500; color: #0f172a; font-size: 13px; }
                .ka-bakiye-neg { font-weight: 500; color: #dc2626; font-size: 13px; }
                .ka-bakiye-currency { font-size: 11px; color: #94a3b8; font-weight: 500; margin-left: 3px; }
                .ka-cell-actions { width: 90px; text-align: right; }

                /* Pill */
                .ka-pill {
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    padding: 4px 11px 4px 8px;
                    border-radius: 999px;
                    font-size: 12px;
                    font-weight: 600;
                }
                .ka-pill-aktif { background: #dcfce7; color: #15803d; }
                .ka-pill-pasif { background: #fee2e2; color: #b91c1c; }
                .ka-pill-dot { width: 7px; height: 7px; border-radius: 50%; flex-shrink: 0; }
                .ka-pill-aktif .ka-pill-dot { background: #22c55e; }
                .ka-pill-pasif .ka-pill-dot { background: #ef4444; }

                /* Ghost buttons */
                .ka-actions {
                    display: flex;
                    justify-content: flex-end;
                    gap: 6px;
                    opacity: 0;
                    transition: opacity 0.15s;
                }
                .ka-row-hovered .ka-actions { opacity: 1; }
                .ka-ghost-btn {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    width: 34px;
                    height: 34px;
                    border-radius: 8px;
                    border: 1.5px solid transparent;
                    background: transparent;
                    cursor: pointer;
                    transition: background 0.15s, border-color 0.15s;
                    color: #64748b;
                }
                .ka-ghost-edit:hover { background: #fff8f0; border-color: #f5d5a0; color: #cd6906; }
                .ka-ghost-delete:hover { background: #fff1f2; border-color: #fecdd3; color: #e11d48; }
                .ka-ghost-btn svg {
                    width: 15px; height: 15px;
                    stroke: currentColor; stroke-width: 2;
                    fill: none; stroke-linecap: round; stroke-linejoin: round;
                }

                /* Footer */
                .ka-footer {
                    padding: 13px 20px;
                    border-top: 1.5px solid #f1f5f9;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                }
                .ka-count { font-size: 13px; color: #94a3b8; font-weight: 500; }
                .ka-count span { color: #475569; font-weight: 600; }

                /* Empty */
                .ka-empty { text-align: center; padding: 56px 20px; color: #94a3b8; }
                .ka-empty-icon { font-size: 36px; margin-bottom: 10px; opacity: 0.5; }
                .ka-empty-text { font-size: 15px; font-weight: 500; }

                /* ── Modal ── */
                .ka-overlay {
                    position: fixed;
                    inset: 0;
                    background: rgba(0,0,0,0.2);
                    backdrop-filter: blur(2px);
                    display: flex; justify-content: flex-end;
                    z-index: 1050;
                }
                .ka-modal {
                    background: #fff;
                    border-radius: 16px 0 0 16px;
                    width: 480px; max-width: 92vw;
                    height: 100%;
                    box-shadow: -8px 0 40px rgba(0,0,0,0.12);
                    animation: kaDrawerIn 0.28s cubic-bezier(0.32,0.72,0,1);
                    display: flex; flex-direction: column;
                    overflow: hidden;
                }
                @keyframes kaDrawerIn {
                    from { transform: translateX(100%); }
                    to   { transform: translateX(0); }
                }
                    to   { opacity: 1; transform: translateY(0) scale(1); }
                }
                .ka-modal-header {
                    padding: 20px 24px;
                    border-bottom: 1px solid #f3f4f6;
                    flex-shrink: 0;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                }
                .ka-modal-title { font-size: 15px; font-weight: 500; color: #0f172a; margin: 0; }
                .ka-modal-close {
                    width: 32px; height: 32px;
                    border-radius: 8px; border: none;
                    background: none; color: #9ca3af;
                    cursor: pointer;
                    display: flex; align-items: center; justify-content: center;
                    font-size: 16px; line-height: 1;
                    transition: background 0.15s;
                }
                .ka-modal-close:hover { background: #f3f4f6; color: #374151; }
                .ka-modal-body { padding: 24px; display: flex; flex-direction: column; gap: 20px; flex: 1; overflow-y: auto; }
                .ka-field label {
                    display: block;
                    font-size: 12px; font-weight: 600;
                    letter-spacing: 0.5px;
                    color: #64748b; margin-bottom: 6px;
                }
                .ka-field input[type="text"],
                .ka-field select {
                    width: 100%;
                    padding: 10px 14px;
                    border: 1.5px solid #e2e8f0;
                    border-radius: 9px;
                    font-size: 14px;
                    font-family: inherit;
                    color: #0f172a;
                    outline: none;
                    transition: border-color 0.15s, box-shadow 0.15s;
                    box-sizing: border-box;
                    background: #fff;
                }
                .ka-field input:focus,
                .ka-field select:focus {
                    border-color: #cd6906;
                    box-shadow: 0 0 0 3px rgba(205,105,6,0.12);
                }
                .ka-field input[disabled] {
                    background: #f8fafc; color: #94a3b8; cursor: not-allowed;
                }
                .ka-modal-footer {
                    padding: 16px 24px;
                    border-top: 1px solid #f3f4f6;
                    background: rgba(255,255,255,0.9); backdrop-filter: blur(8px); flex-shrink: 0;
                    display: flex; justify-content: flex-end; gap: 10px;
                }
                .ka-btn-cancel {
                    padding: 9px 18px; border-radius: 8px; border: none;
                    background: none; color: #4b5563;
                    font-size: 13px; font-weight: 500; font-family: inherit;
                    cursor: pointer; transition: background 0.15s;
                }
                .ka-btn-cancel:hover { background: #f9fafb; }
                .ka-btn-save {
                    padding: 9px 22px;
                    border-radius: 9px; border: none;
                    background: #cd6906; color: #fff;
                    font-size: 13px; font-weight: 600; font-family: inherit;
                    cursor: pointer;
                    box-shadow: 0 2px 8px rgba(205,105,6,0.3);
                    transition: background 0.15s, transform 0.1s;
                }
                .ka-btn-save:hover { background: #b85d05; transform: translateY(-1px); }

                /* ── Responsive ── */
                @media (max-width: 991px) {
                    .ka-page { padding: 18px 14px; }
                    .ka-header { flex-wrap: wrap; gap: 12px; }
                    .ka-stats { grid-template-columns: 1fr 1fr; }
                    .ka-card { overflow-x: auto; -webkit-overflow-scrolling: touch; }
                    .ka-table { min-width: 700px; }
                    .ka-footer { flex-wrap: wrap; gap: 8px; }
                }

                @media (max-width: 576px) {
                    .ka-page { padding: 14px 8px; }
                    .ka-stats { grid-template-columns: 1fr; }
                    .ka-stat-value { font-size: 16px; }
                    .ka-title { font-size: 18px; }
                    .ka-table thead th { padding: 10px 12px; }
                    .ka-table tbody td { padding: 12px; font-size: 13px; }
                }
            `}</style>

            <div style={{ fontFamily: "'Inter', -apple-system, sans-serif", padding: '24px', background: '#fff' }}>
                {/* Header */}
                <div style={{ paddingBottom: 20, marginBottom: 24, borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div>
                        <h1 style={{ fontSize: 20, fontWeight: 600, color: '#111827', margin: 0, lineHeight: 1.3 }}>Kasalar</h1>
                        <p style={{ fontSize: 13, color: '#6b7280', margin: '3px 0 0' }}>Nakit ve banka kasalarını yönetin</p>
                    </div>
                    <button
                        onClick={handleShowModal}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px', background: '#cd6906', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer', boxShadow: '0 1px 3px rgba(205,105,6,0.3)', transition: 'all 0.15s' }}
                        onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = '#a85505'; (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 4px 12px rgba(205,105,6,0.4)'; }}
                        onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = '#cd6906'; (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 1px 3px rgba(205,105,6,0.3)'; }}
                    >
                        <svg style={{ width: 14, height: 14, stroke: '#fff', strokeWidth: 2.5, fill: 'none' }} viewBox="0 0 24 24"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                        Yeni Kasa
                    </button>
                </div>

                {/* Stat Cards */}
                <div className="ka-stats">
                    <div className="ka-stat-card">
                        <div className="ka-stat-icon ka-stat-icon-green">
                            <svg viewBox="0 0 24 24"><rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/></svg>
                        </div>
                        <div className="ka-stat-body">
                            <span className="ka-stat-label">Toplam Nakit</span>
                            <span className="ka-stat-value">{fmtTL(toplamNakit)}</span>
                        </div>
                    </div>
                    <div className="ka-stat-card">
                        <div className="ka-stat-icon ka-stat-icon-blue">
                            <svg viewBox="0 0 24 24"><rect x="3" y="3" width="18" height="18" rx="3"/><path d="M3 9h18M9 21V9"/></svg>
                        </div>
                        <div className="ka-stat-body">
                            <span className="ka-stat-label">Banka Bakiyesi</span>
                            <span className="ka-stat-value">{fmtTL(bankaBakiye)}</span>
                        </div>
                    </div>
                    <div className="ka-stat-card">
                        <div className="ka-stat-icon ka-stat-icon-amber">
                            <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="9"/><path d="M12 6v6l4 2"/></svg>
                        </div>
                        <div className="ka-stat-body">
                            <span className="ka-stat-label">Döviz Toplamı</span>
                            <span className="ka-stat-value">{fmtNum(dovizToplam)}</span>
                        </div>
                    </div>
                </div>

                {/* Card List */}
                <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
                    {kasalar.length === 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '48px 20px', color: '#9ca3af' }}>
                            <div style={{ fontSize: 32, marginBottom: 8, opacity: 0.5 }}>🏦</div>
                            <p style={{ fontSize: 14, fontWeight: 500, margin: 0 }}>Henüz kayıt eklenmedi</p>
                        </div>
                    ) : (
                        kasalar.map((kasa, idx) => {
                            const bank = isBank(kasa);
                            return (
                                <div
                                    key={kasa.kasaID}
                                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', borderBottom: idx < kasalar.length - 1 ? '1px solid #f3f4f6' : 'none', transition: 'background 0.13s' }}
                                    onMouseEnter={e => (e.currentTarget.style.background = '#f9fafb')}
                                    onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                        <div style={{ width: 34, height: 34, borderRadius: 9, background: bank ? '#dbeafe' : '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                            {bank ? (
                                                <svg style={{ width: 16, height: 16, fill: 'none', stroke: '#2563eb', strokeWidth: 1.8, strokeLinecap: 'round', strokeLinejoin: 'round' }} viewBox="0 0 24 24"><rect x="3" y="3" width="18" height="18" rx="3"/><path d="M3 9h18M9 21V9"/></svg>
                                            ) : (
                                                <svg style={{ width: 16, height: 16, fill: 'none', stroke: '#16a34a', strokeWidth: 1.8, strokeLinecap: 'round', strokeLinejoin: 'round' }} viewBox="0 0 24 24"><rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/></svg>
                                            )}
                                        </div>
                                        <div>
                                            <p style={{ fontSize: 14, fontWeight: 600, color: '#111827', margin: 0 }}>{kasa.kasaAdi}</p>
                                            <p style={{ fontSize: 12, color: '#6b7280', margin: '2px 0 0' }}>
                                                {kasa.kasaTuru === 'Banka' ? 'Banka' : 'Nakit'}{kasa.paraBirimi ? ` • ${kasa.paraBirimi}` : ''}{kasa.sorumlu ? ` • ${kasa.sorumlu}` : ''}
                                            </p>
                                        </div>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <span style={{ fontSize: 14, fontWeight: 700, color: kasa.bakiye < 0 ? '#dc2626' : '#111827' }}>
                                            {fmtNum(kasa.bakiye)}{kasa.paraBirimi ? ` ${kasa.paraBirimi}` : ' ₺'}
                                        </span>
                                        <button title="Düzenle" onClick={() => handleEdit(kasa)}
                                            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 6, borderRadius: 6, color: '#2563eb', display: 'flex', transition: 'background 0.13s' }}
                                            onMouseEnter={e => (e.currentTarget.style.background = '#eff6ff')}
                                            onMouseLeave={e => (e.currentTarget.style.background = 'none')}
                                        >
                                            <svg style={{ width: 15, height: 15, stroke: 'currentColor', strokeWidth: 2, fill: 'none' }} viewBox="0 0 24 24"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                                        </button>
                                        <button title="Sil" onClick={() => handleKasaSil(kasa)}
                                            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 6, borderRadius: 6, color: '#f43f5e', display: 'flex', transition: 'background 0.13s' }}
                                            onMouseEnter={e => (e.currentTarget.style.background = '#fff1f2')}
                                            onMouseLeave={e => (e.currentTarget.style.background = 'none')}
                                        >
                                            <svg style={{ width: 15, height: 15, stroke: 'currentColor', strokeWidth: 2, fill: 'none' }} viewBox="0 0 24 24"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
                                        </button>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            </div>

            {/* Modal */}
            {showModal && (
                <div className="ka-overlay" onClick={handleCloseModal}>
                    <div className="ka-modal" onClick={e => e.stopPropagation()}>
                        <div style={{ padding: '20px 24px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
                            <h2 style={{ fontSize: 18, fontWeight: 600, color: '#111827', margin: 0 }}>{selectedKasa ? 'Kasa Düzenle' : 'Yeni Kasa Ekle'}</h2>
                            <button style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '6px', borderRadius: 8, color: '#9ca3af', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32 }} onClick={handleCloseModal}>✕</button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="ka-modal-body">
                                <div>
                                    <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Kasa Adı</label>
                                    <input
                                        type="text"
                                        placeholder="Merkez Kasa, Ziraat Bankası..."
                                        value={formData.kasaAdi}
                                        onChange={e => setFormData({ ...formData, kasaAdi: e.target.value })}
                                        required
                                        maxLength={50}
                                        autoFocus
                                        style={{ width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, fontFamily: 'inherit', color: '#111827', outline: 'none', boxSizing: 'border-box' as const, transition: 'border-color 0.15s, box-shadow 0.15s' }}
                                        onFocus={e => { e.target.style.borderColor = '#111827'; e.target.style.boxShadow = '0 0 0 3px rgba(17,24,39,0.08)'; }}
                                        onBlur={e => { e.target.style.borderColor = '#d1d5db'; e.target.style.boxShadow = 'none'; }}
                                    />
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Kasa Türü</label>
                                    <select
                                        value={formData.kasaTuru}
                                        onChange={e => setFormData({ ...formData, kasaTuru: parseInt(e.target.value) })}
                                        required
                                        style={{ width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, fontFamily: 'inherit', color: '#111827', outline: 'none', boxSizing: 'border-box' as const, background: '#fff', transition: 'border-color 0.15s, box-shadow 0.15s' }}
                                        onFocus={e => { e.target.style.borderColor = '#111827'; e.target.style.boxShadow = '0 0 0 3px rgba(17,24,39,0.08)'; }}
                                        onBlur={e => { e.target.style.borderColor = '#d1d5db'; e.target.style.boxShadow = 'none'; }}
                                    >
                                        <option value={0}>Nakit</option>
                                        <option value={1}>Banka</option>
                                    </select>
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Para Birimi</label>
                                    <select
                                        value={formData.paraBirimiID}
                                        onChange={e => setFormData({ ...formData, paraBirimiID: parseInt(e.target.value) })}
                                        required
                                        style={{ width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, fontFamily: 'inherit', color: '#111827', outline: 'none', boxSizing: 'border-box' as const, background: '#fff', transition: 'border-color 0.15s, box-shadow 0.15s' }}
                                        onFocus={e => { e.target.style.borderColor = '#111827'; e.target.style.boxShadow = '0 0 0 3px rgba(17,24,39,0.08)'; }}
                                        onBlur={e => { e.target.style.borderColor = '#d1d5db'; e.target.style.boxShadow = 'none'; }}
                                    >
                                        <option value="">Seçiniz</option>
                                        {paraBirimleri.map(b => (
                                            <option key={b.paraBirimiID} value={b.paraBirimiID}>{b.birim}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Sorumlu</label>
                                    <select
                                        value={formData.kullaniciID}
                                        onChange={e => setFormData({ ...formData, kullaniciID: parseInt(e.target.value) })}
                                        required
                                        style={{ width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, fontFamily: 'inherit', color: '#111827', outline: 'none', boxSizing: 'border-box' as const, background: '#fff', transition: 'border-color 0.15s, box-shadow 0.15s' }}
                                        onFocus={e => { e.target.style.borderColor = '#111827'; e.target.style.boxShadow = '0 0 0 3px rgba(17,24,39,0.08)'; }}
                                        onBlur={e => { e.target.style.borderColor = '#d1d5db'; e.target.style.boxShadow = 'none'; }}
                                    >
                                        <option value="">Seçiniz</option>
                                        {kullanicilar.map(k => (
                                            <option key={k.kullaniciID} value={k.kullaniciID}>{k.adSoyad}</option>
                                        ))}
                                    </select>
                                </div>
                                {selectedKasa && (
                                    <div>
                                        <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Mevcut Bakiye</label>
                                        <input
                                            type="text"
                                            value={`${fmtNum(selectedKasa.bakiye)} ${selectedKasa.paraBirimi || ''}`}
                                            disabled
                                            style={{ width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, fontFamily: 'inherit', color: '#9ca3af', background: '#f9fafb', outline: 'none', boxSizing: 'border-box' as const, cursor: 'not-allowed' }}
                                        />
                                    </div>
                                )}
                            </div>
                            <div style={{ padding: '16px 24px', borderTop: '1px solid #f3f4f6', display: 'flex', justifyContent: 'flex-end', gap: 10, background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(8px)', flexShrink: 0 }}>
                                <button
                                    type="button"
                                    style={{ padding: '9px 20px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', color: '#374151', fontSize: 13, fontWeight: 500, fontFamily: 'inherit', cursor: 'pointer' }}
                                    onMouseEnter={e => (e.currentTarget.style.background = '#f9fafb')}
                                    onMouseLeave={e => (e.currentTarget.style.background = '#fff')}
                                    onClick={handleCloseModal}
                                >İptal</button>
                                <button
                                    type="submit"
                                    style={{ padding: '9px 22px', borderRadius: 8, border: 'none', background: '#cd6906', color: '#fff', fontSize: 13, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 7, boxShadow: '0 1px 3px rgba(205,105,6,0.3)' }}
                                    onMouseEnter={e => { e.currentTarget.style.background = '#a85505'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(205,105,6,0.4)'; }}
                                    onMouseLeave={e => { e.currentTarget.style.background = '#cd6906'; e.currentTarget.style.boxShadow = '0 1px 3px rgba(205,105,6,0.3)'; }}
                                >
                                    {selectedKasa ? 'Güncelle' : 'Kaydet'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </>
    );
};

// Tiny helper to track per-row hover without polluting parent state
const HoveredRow: React.FC<{
    onEdit: () => void;
    children: (hovered: boolean, setHov: (v: boolean) => void) => React.ReactNode;
}> = ({ children }) => {
    const [hov, setHov] = useState(false);
    return (
        <tr
            className={hov ? 'ka-row-hovered' : ''}
            onMouseEnter={() => setHov(true)}
            onMouseLeave={() => setHov(false)}
        >
            {children(hov, setHov)}
        </tr>
    );
};

export default Kasalar;
